import { forEach } from 'lodash';
import { snapshot, subscribe } from 'valtio';
import type { MapObject } from '../../../common/types';
import * as ImageCache from '../ImageCache';
import ArrowObject from './arrow/ArrowObject';
import { HoverEvent } from './CanvasEvent';
import type CanvasObject from './CanvasObject';
import CircleObject from './circle/CircleObject';
import CommentObject from './comment/CommentObject';
import ImageObject from './image/ImageObject';
import LineObject from './line/LineObject';
import MarkerObject from './marker/MarkerObject';
import PolygonObject from './polygon/PolygonObject';
import SurfaceBase, { type SurfaceBaseOptions } from './SurfaceBase';
import StampObject from './stamp/StampObject';
import StreetViewMarkerObject from './streetViewMarker/StreetViewMarkerObject';
import TextObject from './text/TextObject';
import TextObjectV2 from './textV2/TextObject';

interface SurfaceOptions extends SurfaceBaseOptions {}

export default class Surface extends SurfaceBase {
  constructor(options: SurfaceOptions) {
    super(options);

    let lastStoreSnapshot = snapshot(this.getStore());

    this.cleanupFns.push(
      subscribe(this.getStore(), () => {
        const currentSnapshot = snapshot(this.getStore());
        if (currentSnapshot.editorMode !== lastStoreSnapshot.editorMode) {
          this.redraw();
        } else if (
          Object.keys(currentSnapshot.objectsOnMainSurface).length !==
          Object.keys(lastStoreSnapshot.objectsOnMainSurface).length
        ) {
          this.redraw();
        } else if (currentSnapshot.hiddenObjects !== lastStoreSnapshot.hiddenObjects) {
          this.redraw();
        } else if (
          (currentSnapshot.currentMap.isLoading === false && currentSnapshot.currentMap.unitSystem) !==
          (lastStoreSnapshot.currentMap.isLoading === false && lastStoreSnapshot.currentMap.unitSystem)
        ) {
          this.redraw();
        } else if (currentSnapshot.shouldRedraw) {
          this.getStore().shouldRedraw = false;
          this.redraw();
        }
        lastStoreSnapshot = currentSnapshot;
      }),
    );

    this.drawAllObjects();
  }

  destroy(): void {
    super.destroy();
    ImageCache.clearCache();
  }

  redraw = () => {
    this.removeAllObjects();
    this.drawAllObjects();
  };

  drawAllObjects = () => {
    forEach(this.getStore().objectsOnMainSurface, (object: MapObject, id) => {
      let drawingObject: CanvasObject;

      // do not show objects that are not visible
      if (this.getStore().hiddenObjects.includes(id)) return;

      if (id === this.getStore().currentObjectId) {
        // skip object that is currently being edited.
        // It will be rendered in the EditorSurface
      } else {
        const o = snapshot(object) as typeof object;
        if (o.type === 'polygon') {
          drawingObject = new PolygonObject(id, this, o);
        } else if (o.type === 'line') {
          drawingObject = new LineObject(id, this, o);
        } else if (o.type === 'marker') {
          drawingObject = new MarkerObject(id, this, o);
        } else if (o.type === 'text') {
          drawingObject = new TextObject(id, this, o);
        } else if (o.type === 'comment') {
          drawingObject = new CommentObject(id, this, o);
        } else if (o.type === 'textV2') {
          drawingObject = new TextObjectV2(id, this, o);
        } else if (o.type === 'image') {
          // skip images that have no files
          if (!o.files?.length) return;
          drawingObject = new ImageObject(id, this, o);
        } else if (o.type === 'stamp') {
          drawingObject = new StampObject(id, this, o);
        } else if (o.type === 'streetViewMarker') {
          drawingObject = new StreetViewMarkerObject(id, this, o);
        } else if (o.type === 'arrow') {
          drawingObject = new ArrowObject(id, this, o);
        } else if (o.type === 'circle') {
          drawingObject = new CircleObject(id, this, o);
        }

        drawingObject!.sortOrder = o.sortOrder;
        drawingObject!.interactive = true;
        drawingObject!.on('hoverStart', () => {
          this.setCursor('pointer');
        });

        drawingObject!.on('hoverEnd', () => {
          this.resetCursor();
        });

        if (o.type === 'streetViewMarker') {
          drawingObject!.on('tap', () => {
            this.getStore().setStreetViewState({
              visible: true,
              isNew: false,
              marker: o,
            });
          });
        } else {
          drawingObject!.on('tap', () => {
            // need to send hoverEnd event to cancel image preview if it is open.
            if (o.type === 'image') {
              drawingObject.emit(
                'hoverEnd',
                new HoverEvent('hoverEnd', {
                  point: { x: NaN, y: NaN },
                  lngLat: { lng: NaN, lat: NaN },
                }),
              );
            }
            this.getStore().setCurrentObjectId(drawingObject!.id);
          });
        }

        this.addObjectWithoutRender(drawingObject!);
      }
    });
    this.renderAll();
  };
}
